import { UpsellBillingTrigger } from "@redotech/congo-enums/upsell-billing-trigger";
import { Currency } from "@redotech/money/currencies";
import { CustomizationContext } from "@redotech/redo-customer-portal-app/customization/customization-context";
import { CustomerPortalAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import { ReturnItemParams } from "@redotech/redo-customer-portal-shared/util";
import { useTranslation } from "@redotech/redo-i18n";
import { getShopperShippingFeeLines } from "@redotech/redo-return-core-common/returns/return-totals/shopper-shipping-fee-lines";
import { SerializedReturnSummaryTotals } from "@redotech/redo-return-core-common/returns/return-totals/types";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { capitalizeWords } from "@redotech/string/util";
import { useContext } from "react";
import { SummaryCard } from "../../submission/components/summary-card";
import { confirmationItemTitle } from "./confirmation-item-title";

export function SummaryCardConfirmation({
  totals,
  currency,
  feesHeader,
  feesOrderHeader,
  products,
  newItems,
  labelDeducted,
  isLabelCovered = false,
  settings,
  upsellBillingTrigger,
  isConvenienceFee,
  chargesExchangeShippingViaStripe,
  exchangeShippingName,
  hideItemCosts = false,
  isWarranty = false,
  originalOrderShippingAmount = 0,
  outstandingBalanceManuallyWaived,
}: {
  feesHeader: string;
  feesOrderHeader: string;
  totals: SerializedReturnSummaryTotals;
  currency: Currency;
  products: ReturnItemParams[] | undefined;
  newItems: ReturnItemParams[] | undefined;
  labelDeducted: boolean;
  isLabelCovered?: boolean;
  settings: CustomerPortalAppSettings | undefined;
  upsellBillingTrigger?: UpsellBillingTrigger | null;
  isConvenienceFee: boolean;
  /** Not carried by RAC totals — read from the return. */
  chargesExchangeShippingViaStripe: boolean;
  exchangeShippingName: string;
  hideItemCosts?: boolean;
  isWarranty?: boolean;
  originalOrderShippingAmount?: number;
  outstandingBalanceManuallyWaived?: boolean;
}) {
  const { t } = useTranslation();
  const { customization } = useContext(CustomizationContext);
  const adjustmentText = customization?.pages?.review?.text;
  const { isLoading, productSubtotals } = getConfirmationSummary({ products });

  const {
    isLoading: isLoadingNewItems,
    productSubtotals: newOrderItemsSubtotals,
  } = getConfirmationSummary({ products: newItems });

  if (isLoading || isLoadingNewItems) {
    return <LoadingRedoAnimation />;
  }
  const bonusText =
    adjustmentText?.adjustmentPositiveName ||
    settings?.returnAdjustment?.positiveName;
  const feeText =
    adjustmentText?.adjustmentNegativeName ||
    settings?.returnAdjustment?.negativeName;
  const totalAdjustmentValue =
    totals.adjustmentTotals.totalFlowAdjustments ??
    totals.adjustmentTotals.totalNetAdjustmentValue;
  const adjustmentName = totalAdjustmentValue > 0 ? bonusText : feeText;
  const refundsOriginalShipping = originalOrderShippingAmount > 0;
  const totalRefund = refundsOriginalShipping
    ? totals.refund.withOriginalOrderShipping
    : totals.refund.withoutOriginalOrderShipping;
  const totalStoreCredit = refundsOriginalShipping
    ? totals.storeCredit.withOriginalOrderShipping
    : totals.storeCredit.withoutOriginalOrderShipping;
  const returnShippingFee = totals.returnShippingFee.amountPaidWithCard;
  const exchangeShippingFee = totals.newOrderTotals.exchangeShippingFee;
  const shippingFeeLines = (amount: number) =>
    getShopperShippingFeeLines({
      amount,
      shopperShippingFee: totals.returnShippingFee.totalAmount,
      carrierUpgradeFee:
        totals.returnShippingFee.carrierUpgradeFeeComponent ?? 0,
      isLabelCovered,
    });
  const paidWithCardLines = shippingFeeLines(returnShippingFee);
  const deductedLines = shippingFeeLines(
    totals.returnShippingFee.amountDeducted.fromStoreCreditAndRefund,
  );

  return (
    <SummaryCard
      adjustmentValue={totalAdjustmentValue}
      adjustmentValueString={capitalizeWords(adjustmentName ?? "")}
      carrierUpgradeFeeComponent={
        paidWithCardLines.carrierUpgrade + deductedLines.carrierUpgrade
      }
      chargesExchangeShippingViaStripe={chargesExchangeShippingViaStripe}
      currency={currency}
      exchangeLandedCost={totals.newOrderTotals.exchangeLandedCost}
      exchangeShippingFee={exchangeShippingFee}
      exchangeShippingName={exchangeShippingName}
      feesHeader={feesHeader}
      feesOrderHeader={feesOrderHeader}
      hideItemCosts={hideItemCosts}
      isConvenienceFee={isConvenienceFee}
      isWarranty={isWarranty}
      labelDeducted={labelDeducted}
      newOrderItemsSubtotals={newOrderItemsSubtotals}
      newOrderTaxes={[
        {
          title: t("Taxes"),
          total: totals.newOrderTotals.taxes.forAdvancedExchangeProducts,
        },
      ]}
      newOrderValue={totals.newOrderTotals.totalValue}
      newVariantExchangeItemSubtotals={[]}
      outstandingBalanceManuallyWaived={outstandingBalanceManuallyWaived}
      pickupFee={undefined}
      pointsDeductedFromRefundSubtotal={undefined}
      productSubtotals={productSubtotals}
      returnValue={
        totals.returnValue.amount -
        totalAdjustmentValue +
        originalOrderShippingAmount
      }
      shippingFee={paidWithCardLines.returnShipping}
      shippingFeeDeducted={deductedLines.returnShipping}
      submitted
      taxSubtotals={[
        { title: t("Taxes"), total: totals.returnValue.totalTaxes },
        ...(totals.returnValue.totalDuty
          ? [{ title: t("Duties"), total: totals.returnValue.totalDuty }]
          : []),
        ...(refundsOriginalShipping
          ? [{ title: t("Shipping"), total: originalOrderShippingAmount }]
          : []),
      ]}
      totalPaymentOwed={totals.totalPaymentOwed}
      totalRefund={totalRefund}
      totalShippingFee={returnShippingFee + exchangeShippingFee}
      totalStoreCredit={totalStoreCredit}
      upsellBillingTrigger={upsellBillingTrigger}
    />
  );
}

interface GetConfirmationSummaryParams {
  products: ReturnItemParams[] | undefined;
}

interface GetConfirmationSummaryResponse {
  isLoading: boolean;
  productSubtotals: { title: string; total: number }[];
}

function getConfirmationSummary({
  products,
}: GetConfirmationSummaryParams): GetConfirmationSummaryResponse {
  const productSubtotals =
    products?.map((product) => {
      return {
        title: confirmationItemTitle(product),
        total:
          +(product.itemValue ?? product.price) *
          product.quantity *
          (product.exchangeRate ?? 1),
      };
    }) ?? [];

  return { isLoading: !products, productSubtotals };
}
